/**
 * liveQuizTracking — ce que l'élève fait de l'offre du quiz de fin de séance. `screen.viewed.live-quiz`
 * part avant de savoir s'il y a un quiz : seule l'offre montrée dit qu'il l'a eue sous les yeux.
 *
 *  - `end_of_live_quiz.offer_shown` : l'offre s'affiche ;
 *  - `end_of_live_quiz.start_clicked` : « Je commence » ;
 *  - `end_of_live_quiz.declined` : le second bouton (`via` `button`), le retour d'Android (`back`), ou
 *    l'écran quitté sans geste, le retour du navigateur par exemple (`left`).
 *
 * Une offre montrée sans rien après, c'est une app ou un onglet fermé : rien ne s'exécute alors. Une
 * page rechargée remontre la même offre : un taux se compte en `quiz_id` distincts.
 */
import { useEffect, useRef } from "react"

import { track } from "../../../hooks/trackEvent"

const LIVE_QUIZ_EVENTS = {
    offerShown: "end_of_live_quiz.offer_shown",
    startClicked: "end_of_live_quiz.start_clicked",
    declined: "end_of_live_quiz.declined",
}

export type LiveQuizOffer = { quizId: number; total: number; minutes: number; missed: number; petals: number }

export type LiveQuizDeclineVia = "button" | "back"

function offerProperties(offer: LiveQuizOffer, exitTo: string, answered: number): Record<string, string> {
    return {
        quiz_id: String(offer.quizId),
        exit_to: exitTo,
        total: String(offer.total),
        minutes: String(offer.minutes),
        missed: String(offer.missed),
        petals: String(offer.petals),
        answered: String(answered),
    }
}

/** `offer` : l'offre à l'écran, `null` avant qu'elle s'affiche et une fois le quiz commencé. */
export function useLiveQuizOfferTracking(offer: LiveQuizOffer | null, exitTo: string, answered: number) {
    /** L'offre montrée à laquelle aucun geste n'a encore répondu : un seul geste se mesure par offre. */
    const unansweredRef = useRef<Record<string, string> | null>(null)

    const shown = offer != null
    useEffect(() => {
        if (offer == null) return
        const properties = offerProperties(offer, exitTo, answered)
        unansweredRef.current = properties
        track({ name: LIVE_QUIZ_EVENTS.offerShown, properties })
        // Le seul signal du retour du navigateur : `BackHandler` ne l'entend pas au web.
        return () => {
            if (unansweredRef.current != null) {
                track({ name: LIVE_QUIZ_EVENTS.declined, properties: { ...unansweredRef.current, via: "left" } })
            }
        }
        // Une fois, quand l'offre arrive : elle ne revient pas une fois le quiz commencé.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shown])

    const answer = (name: string, extra: Record<string, string> = {}) => {
        if (unansweredRef.current == null) return
        track({ name, properties: { ...unansweredRef.current, ...extra } })
        unansweredRef.current = null
    }

    return {
        trackStart: () => answer(LIVE_QUIZ_EVENTS.startClicked),
        trackDecline: (via: LiveQuizDeclineVia) => answer(LIVE_QUIZ_EVENTS.declined, { via }),
    }
}
